/* eslint-disable array-callback-return */
import { Box, Button, Grid, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as yup from 'yup';
import Header from '../../components/Header';
import { useCookiesLogin } from '../../config/cookies';
import { HOST_REDUTO } from '../../config/hosts';
import { tokens } from '../../theme';
import FormSerasa from './formSerasa';

// Regex
const cpfCnpjRegex = /^(\d{3})(\d{3})(\d{3})(\d{2})$|^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/;

const regexCNPJ = /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/;

// Numeric input format
const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator="."
      decimalSeparator=","
      decimalScale={2}
      fixedDecimalScale
      valueIsNumericString
      prefix="R$ "
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Validation Schema
const RedSimulationSchema = yup.object().shape({
  amount: yup.number().required('Campo obrigatório'),
  tax: yup.number().nullable(),
});

const initialValuesRedSimulation = {
  amount: '',
  tax: '',
};

const RedSimulation = ({ title, simulationUri, type_simulation, description, redrural }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { cookies } = useCookiesLogin();
  const [algorithm, setAlgorithm] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [errorAmount, setErrorAmount] = useState(false);
  const [showFormSerasa, setShowFormSerasa] = useState(false);
  const [showButtonFormSerasa, setShowButtonFormSerasa] = useState(true);
  const [amount, setAmount] = useState(0);
  const [display, setDisplay] = useState(false);

  useEffect(() => {
    document.title = `RED 25 - Análise`;
  }, []);

  const handleRowSelection = (newSelection) => {
    if (newSelection.length === 1) {
      setSelectedRow(newSelection[0]);
      const selectedRow = algorithm.find((row) => row.id === newSelection[0]);
      setSelectedRowData(selectedRow);
    } else {
      setSelectedRow(null);
      setSelectedRowData({});
    }
  };

  async function handleRedSimulation(amount, tax) {
    setAmount(amount);

    const url = `${HOST_REDUTO}/reduto/red25/simulate`;

    const data = {
      amount: parseFloat(amount),
      type_simulation: type_simulation,
      tax: tax,
    };

    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((result) => {
        const objectWithIds = result.steps.map((data, index) => ({
          id: index + 1,
          ...data,
        }));
        setAlgorithm(objectWithIds);
        setDisplay(true);
      })
      .catch(() => {
        toast.error('Erro ao simular. Tente novamente ou entre em contato com o suporte', {
          theme: 'dark',
        });
      });
  }

  function handleErrorAmount() {
    setTimeout(() => {
      setErrorAmount(false);
    }, 5000);
    return (
      <Typography color="error" mt={1}>
        Valor inválido, por favor insira o valor corretamente.
      </Typography>
    );
  }

  let label = 'Valor presente';

  return (
    <Box m={3}>
      <Header
        title={title}
        subtitle={redrural ? description : 'Análises de crédito equipe Reduto'}
      />

      <Formik initialValues={initialValuesRedSimulation} validationSchema={RedSimulationSchema}>
        {({ errors, touched, values, handleBlur, handleChange }) => (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (values.amount === 0) {
                setErrorAmount(true);
                return;
              }
              await handleRedSimulation(values.amount, values.tax);
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  variant="filled"
                  label={label}
                  color="secondary"
                  value={values.amount}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="amount"
                  InputProps={{ inputComponent: NumericFormatCustom }}
                  error={!!touched.amount && !!errors.amount}
                  helperText={touched.amount && errors.amount}
                />
                {errorAmount && handleErrorAmount()}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Simular com taxa real"
                  color="secondary"
                  value={values.tax}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const normalizedValue = rawValue.replace(',', '.');
                    handleChange({
                      target: {
                        name: 'tax',
                        value: normalizedValue,
                      },
                    });
                  }}
                  name="tax"
                  error={!!touched.tax && !!errors.tax}
                  helperText={touched.tax && errors.tax}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4}>
                <Button
                  type="submit"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 50, width: 120 }}
                >
                  {title}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>

      {display && (
        <Box mt={4}>
          <DataGrid
            rows={algorithm}
            columns={[
              { field: 'id', headerName: 'Qtde. de Duplicatas', width: 160 },
              {
                field: 'pmt_money',
                headerName: 'Valor das Duplicatas',
                width: 200,
              },
              {
                field: 'total_real',
                headerName: 'Valor da Nota Fiscal',
                width: 200,
              },
            ]}
            selectionModel={selectedRow !== null ? [selectedRow] : []}
            onSelectionModelChange={handleRowSelection}
            checkboxSelection
            // disableRowSelectionOnClick
            sx={{
              height: 400,
              border: `2px solid ${colors.grey[500]}`,
              mt: 2,
              '& .MuiDataGrid-cell': {
                color: 'white',
              },
              '& .MuiDataGrid-columnHeader': {
                color: 'white',
              },

              '& .MuiCheckbox-root': {
                color: 'white',
              },
              '& .MuiCheckbox-root.Mui-checked': {
                color: 'white',
              },
              '& .MuiCheckbox-root.MuiCheckbox-indeterminate': {
                color: 'white',
              },
            }}
          />

          {showButtonFormSerasa && (
            <Button
              sx={{ mt: 3 }}
              color="secondary"
              variant="outlined"
              onClick={() => {
                setShowFormSerasa(true);
                setShowButtonFormSerasa(false);
              }}
            >
              Análise de Crédito
            </Button>
          )}
        </Box>
      )}

      {showFormSerasa && (
        <FormSerasa
          algorithm={algorithm}
          installments={selectedRow}
          amount={amount}
          simulationUri={simulationUri}
          title={title}
        />
      )}
    </Box>
  );
};

RedSimulation.propTypes = {
  title: PropTypes.string.isRequired,
  simulationUri: PropTypes.string.isRequired,
  type_simulation: PropTypes.string,
  description: PropTypes.string,
  redrural: PropTypes.bool,
};

export default RedSimulation;
